import {
  ContextProviderNode,
  ContextCategoryNode,
  ContextPropertyNode,
  isContextCategoryNode,
  CategoryType,
  ContextNode,
  isContextPropertyNode,
} from '@decisions/server/DecisionsFramework/Design/Properties';
import { EditorNames } from 'components/Editors/EditorNames';
import { getEditorConfigValue } from './EditorConfigurationUtils';

/**
 * Hooks for communicating with the ContextProviderService
 */
export const NodeTypes = {
  Root: 'ContextRootNode:#DecisionsFramework.Design.Properties',
  Category: 'ContextCategoryNode:#DecisionsFramework.Design.Properties',
  Property: 'ContextPropertyNode:#DecisionsFramework.Design.Properties',
  ListProperty: 'ContextListPropertyNode:#DecisionsFramework.Design.Properties',
};

export interface ICategoryNode extends ContextCategoryNode {
  /**
   * Should we expand it during search?
   * cheating by adding this. this will not come from server, but may be added by search feature client-side
   */
  OpenBySearch?: boolean;
}

export const NO_CATEGORY = '[nocategory]';
export const PROVIDER_NODE_PATH_SEPARATOR = ',';

/**
 * A ContextProviderNode whose "Node" property is a "ContextPropertyNode".
 *
 * (Why can't we know based on the type definitions? Seems disjointed.)
 */

export interface IPropertyChild extends ContextProviderNode {
  Node: ContextPropertyNode;
  className?: string;
}

export function getPropertyDisplayLabel(child: IPropertyChild) {
  return child.Node.DisplayLabel || child.Node.Name;
}

export function getChildValueAsArray(child: ContextProviderNode): any[] {
  const value = child.Value && child.Value.OutputValue ? child.Value.OutputValue : [];
  return Array.isArray(value) ? (value as any[]) : [value];
}

export function isPropertyNode(node: ContextNode): node is ContextPropertyNode {
  return isContextPropertyNode(node);
}

export function getChildValueAsString(child: ContextProviderNode) {
  return child.Value?.OutputValue !== undefined && child.Value?.OutputValue != null
    ? child.Value.OutputValue.toString()
    : '';
}

export function childValueIsNull(child: ContextProviderNode) {
  return (
    !child.Value ||
    child.Value.OutputValue === null ||
    (typeof child.Value.OutputValue === 'string' && (child.Value.OutputValue as string).toUpperCase() === 'NULL')
  );
}

export function getChildValue(child: ContextProviderNode) {
  return child.Value && child.Value.OutputValue;
}

export function getChildValueAsBoolean(child: ContextProviderNode) {
  return !!child.Value && !!child.Value.OutputValue;
}
export function getChildrenFromSnapshot(result?: ContextProviderNode, hideParentCategory = false) {
  if (hideParentCategory && result?.Children && result?.Children[0].Children) {
    // strip out the parent category if it's there
    return result?.Children[0].Children || [];
  }
  return result?.Children || [];
}

/**
 * Comparator for sorting a set of ContextProviderNodes
 *
 * Ordering is based on legacy MVC sorting:
 * ```csharp
 * var sortedValue = Model.ContextProviderNode.Children.OrderBy(t =>
 *  {
 *    if (t.Children != null && t.Children.Length > 0)
 *        return 1;
 *    return 0;
 *  }).ThenBy(p => p.Node.Index).ThenBy(p => p.Node.Name);
 * ````
 * The above could easily be applied server-side, when fetching context provider snapshots,
 * but because the collection is thereafter dynamic (e.g. PropertyHidden attributes) and maintained client side,
 * sorting needs to be done client side as well.
 *
 * Properties before Categories (as observed...)
 * 1. then: categories without children before categories with children
 * 1. then: by index
 * 1. then: by name - square brackets (at least) should be ignored in alphanumeric sort.
 *
 * @param a first node
 * @param b second node
 */
export function compareEditorChildren(a: ContextProviderNode, b: ContextProviderNode): number {
  const bothCategories = isCategory(a.Node) && isCategory(b.Node);
  const bothEditors = !isCategory(a.Node) && !isCategory(b.Node);

  if (bothEditors || bothCategories) {
    if (bothCategories) {
      // prefer NO_CATEGORY to all others:
      if (a.Node.Name === NO_CATEGORY) {
        return -1;
      } else if (b.Node.Name === NO_CATEGORY) {
        return 1;
      }
    }

    const aHasChildren = hasChildren(a);
    const bHasChildren = hasChildren(b);
    // should this children sort also apply to editors, or only to categories?
    if ((aHasChildren && !bHasChildren) || (!aHasChildren && bHasChildren)) {
      return aHasChildren - bHasChildren;
    }
    if (a.Node.Index !== b.Node.Index) {
      return a.Node.Index - b.Node.Index;
    }
    // use toLowerCase here to match .NET OrderBy where `[` would be before `p`, etc.
    let nameA = a.Node.Name.toLocaleLowerCase();
    let nameB = b.Node.Name.toLocaleLowerCase();
    return nameA === nameB ? 0 : nameA < nameB ? -1 : 1;
  } else if (!isCategory(a.Node)) {
    // prefer properties to categories
    return -1;
  }
  return 0;
}

function isCategory(node: ContextNode) {
  return isContextCategoryNode(node) && (node as ContextCategoryNode).CategoryType !== CategoryType.PopupEditor;
}

function hasChildren(node: ContextProviderNode) {
  // even though these categories have children, they should be treated as not, otherwise sort index won't apply when mixed with other editors.
  const specialCategory =
    isContextCategoryNode(node.Node) && (node.Node as ContextCategoryNode).CategoryType === CategoryType.PopupEditor;
  return !specialCategory && node.Children?.length ? 1 : 0;
}

export function categoryIsMappingTypeSelector(child: ContextProviderNode) {
  return (
    child.Children &&
    child.Children[0] &&
    getNodeEditorName(child.Children[0].Node as ContextPropertyNode) === EditorNames.Mappings
  );
}

export function getMappingDataType(node: ContextProviderNode, displayName = false) {
  return categoryIsMappingTypeSelector(node)
    ? getEditorConfigValue(node.Children[0], displayName ? 'type.displayname' : 'type') || ''
    : '';
}

export function getCategoryChildCount(child: ContextProviderNode) {
  return Array.isArray(child.Children) ? child.Children.length : 0;
}

export function getNodeEditorName(child: ContextPropertyNode) {
  return child.MvcEditorName;
}

/**
 * Recursively follow the 'path' through the data model.
 * @param children to search
 * @param path to desired child data
 * @returns child component with its parent
 */
export function findContextChildByPath(
  pathArray: string[],
  parent: ContextProviderNode
): { child: ContextProviderNode | undefined; parent: ContextProviderNode } {
  if (!parent.Children) {
    return { child: undefined, parent };
  }

  // add '...' because the array can be changed by reference
  // for example, we have 2 PGs with 1 context ID - second will get an empty array
  const path = [...pathArray];

  const name = path.shift();
  const child = parent.Children.find(child => child.Node.Name === name);
  if (child && path.length) {
    return findContextChildByPath(path, child);
  }
  return { child, parent };
}

// would like the above/below to not feel like duplication, but the requirement of the parent return in the above
// makes it impossible for them to share. at least they aren't that long

/**
 * Recursively follow the 'path' through a list of nodes.
 * @param children to search
 * @param path to desired child data
 * @returns child component
 */
export function findContextChildInListByPath(
  pathArray: string[],
  list: ContextProviderNode[]
): ContextProviderNode | undefined {
  if (!list || !list.length) {
    return;
  }

  // add '...' because the array can be changed by reference
  // for example, we have 2 PGs with 1 context ID - second will get an empty array
  const path = [...pathArray];

  const name = path.shift();
  const child = list.find(child => child.Node.Name === name);
  if (child && path.length) {
    return findContextChildInListByPath(path, child.Children);
  }
  return child;
}

export function getKeyFromPath(path: string[] = [], child: ContextProviderNode) {
  return [...path, child.Node.Name].join(PROVIDER_NODE_PATH_SEPARATOR);
}

/**
 * Search a ContextProviderNode tree based on string and any provided and/or filters.
 * @param search string to search
 * @param nodes to comb through recursively
 * @param andConditions extra conditions which must be true
 * @param orConditions extra conditions which may be true
 * @param bailForEmptySearch default to `true` meaning immediate return if search is empty. Will skip your `orConditions`
 * @param modifyInitiallyCollapsed true to modify this setting (e.g. MappingEditor), otherwise will add `OpenBySearch` property (e.g. PropertyGrid)
 * @returns filtered `ContextProviderNode[]`
 */
export function providerNodeSearch(
  search: string,
  nodes?: ContextProviderNode[] | IPropertyChild[],
  andConditions: IProviderNodeSearchCondition[] = [],
  orConditions: IProviderNodeSearchCondition[] = [],
  bailForEmptySearch = true,
  modifyInitiallyCollapsed = false,
  expansionTracker = (_path: string, _expanded: boolean) => {}
) {
  if (!search && bailForEmptySearch) {
    return nodes || [];
  }
  if (!Array.isArray(nodes)) {
    return [];
  }

  const filtered: Array<ContextProviderNode | IPropertyChild> = [];

  nodes.forEach((node: ContextProviderNode) => {
    const isCategory = node.Node.__type === NodeTypes.Category;
    const isMappingTypeSelector = categoryIsMappingTypeSelector(node);

    // TODO fuse.js for fuzzy searching?
    let includeNode = !search || nameIncludesSearch(search, node.Node.Name) || displayNameIncludesSearch(search, node);

    // apply orConditions
    includeNode = includeNode || anyOrConditionsTrue(orConditions, node, search, includeNode);
    // apply and conditions
    includeNode = allAndConditionsTrue(andConditions, node, search, includeNode);

    // if it's a mapping type selector, we want all the editor parts, so we only need to search other category children:
    const { kidsToSearch, mappingSelectorParts } = getMappingTypeSelectorPartsForSearch(node, isMappingTypeSelector);

    const childExpansionTracker = (path: string, expanded: boolean) => {
      // wrap as we recurse to get the whole path of the tree, not just the tip
      path = `${node.Node.Name}${PROVIDER_NODE_PATH_SEPARATOR}${path}`;
      expansionTracker(path, expanded);
    };

    // if we already including a matched category then we don`t need filter children, otherwise search them
    let children =
      isCategory && includeNode
        ? []
        : providerNodeSearch(
            search,
            kidsToSearch,
            andConditions,
            orConditions,
            bailForEmptySearch,
            modifyInitiallyCollapsed,
            childExpansionTracker
          );
    const searchMatchedChildren = !!children.length;

    // include if grandchildren are present, even with no child match
    let include = includeNode || searchMatchedChildren; // || anyOrConditionsTrue(orConditions, node, search, includeCategory);

    if (include) {
      // if grandChildren.length === 0 => show all children
      children = getSearchedCategoryNodeChildren(children, node.Children);
      if (isCategory) {
        // if it's a category, set it to be expanded
        const Node = modifyInitiallyCollapsed
          ? { ...node.Node, InitiallyCollapsed: false }
          : { ...node.Node, OpenBySearch: true };
        children = searchMatchedChildren && mappingSelectorParts ? [...mappingSelectorParts, ...children] : children; // ensureMappingNodesPreserved(node, children);

        // if tracking expansion outside node itself
        expansionTracker(node.Node.Name, true);

        filtered.push({ ...node, Node, Children: children });
      } else {
        filtered.push({ ...node, Children: children });
      }
    }
  });

  return filtered;
}

export function displayNameIncludesSearch(search: string, child: ContextProviderNode | IPropertyChild) {
  return (child as IPropertyChild).Node.DisplayLabel
    ? (child as IPropertyChild).Node.DisplayLabel.toUpperCase().includes(search.toUpperCase())
    : false;
}

// test iteration count:
// export const SearchIterationCount = {
//   count: 0,
// };

export function nameIncludesSearch(search: string, childName: string, stripMappingSuffixes = true) {
  return childName.toUpperCase().includes(search.toUpperCase());
}

/**
 * Because MappingTypeSelectors are a category node with special children which
 * - should not affect search results
 * - should not be filtered out during search
 * - differ in count by mapping type
 *
 * and some MappingTypes also have children that Search _should_ apply to, such as Build Data,
 * we need to handle their children in a special way.
 *
 * @param node to evaluate
 * @param isMappingTypeSelector true if it is, otherwise will be evaluated
 * @returns list of nodes particular to the mapping type selector, and a list of children where search should be applied
 */
export function getMappingTypeSelectorPartsForSearch(
  node: ContextProviderNode,
  isMappingTypeSelector = categoryIsMappingTypeSelector(node)
) {
  // if it's a mapping type selector, we want all the editor parts, so we only need to search other category children:
  if (!isMappingTypeSelector) {
    return { kidsToSearch: node.Children, mappingSelectorParts: undefined };
  }

  const mappingSelectorParts: ContextProviderNode[] = [];
  const kidsToSearch = node.Children.filter(child => {
    const isMappingChildCategory = child.Node.__type === NodeTypes.Category;
    if (!isMappingChildCategory) {
      // it's a special node, save for later:
      mappingSelectorParts.push(child);
    }
    return isMappingChildCategory;
  });

  return { kidsToSearch, mappingSelectorParts };
}

export interface IProviderNodeSearchCondition {
  (node: ContextProviderNode, search: string): boolean;
}

export function anyOrConditionsTrue(
  conditions: IProviderNodeSearchCondition[] = [],
  node: ContextProviderNode,
  search: string,
  state: boolean
): boolean {
  let i = 0;
  while (state === false && i < conditions.length) {
    // comparing to false, because with OR we can stop after first match
    state = state || conditions[i](node, search);
    i++;
  }
  return state;
}

export function allAndConditionsTrue(
  conditions: IProviderNodeSearchCondition[] = [],
  node: ContextProviderNode,
  search: string,
  state: boolean
): boolean {
  let i = 0;
  while (state === true && i < conditions.length) {
    // comparing to true, because with AND we can stop after first failure
    state = state && conditions[i](node, search);
    i++;
  }
  return state;
}

export function getSearchedCategoryNodeChildren(
  filteredChildren: (ContextProviderNode | IPropertyChild)[],
  originalChildren?: (ContextProviderNode | IPropertyChild)[]
) {
  // if a category is included by matching it's name, but none of it's
  // children match, then include all of them. we found the category,
  // otherwise you render an empty category
  return filteredChildren.length ? filteredChildren : originalChildren || [];
}

export enum EditorHeightBehavior {
  Unset,
  Fixed,
  Stretch,
}

export function getEditorHeightBehavior(node: ContextProviderNode | IPropertyChild) {
  if (isPropertyNode(node.Node) && !isNaN(node.Node.Height) && node.Node.Height > -1) {
    return node.Node.Height === 0 ? EditorHeightBehavior.Stretch : EditorHeightBehavior.Fixed;
  }
  return EditorHeightBehavior.Unset;
}

export function getEditorHeightStyle(node: ContextProviderNode | IPropertyChild) {
  const behavior = getEditorHeightBehavior(node);
  if (!isPropertyNode(node.Node)) {
    return {};
  }

  switch (behavior) {
    case EditorHeightBehavior.Fixed:
      return { maxHeight: 'unset', height: node.Node.Height };
    case EditorHeightBehavior.Stretch:
      // variable height editors should have a flex-column wrapper, in most cases.
      return { maxHeight: 'unset', flex: '1 1 auto' };
    default:
      return {};
  }
}

export function getEditorWrapperHeightStyle(node: ContextProviderNode | IPropertyChild) {
  const behavior = getEditorHeightBehavior(node);
  if (!isPropertyNode(node.Node)) {
    return;
  }

  switch (behavior) {
    case EditorHeightBehavior.Stretch:
      return '100%';
    default:
      return;
  }
}
